import { useCallback, useMemo, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { rolesEndpoints as endpoints } from '@/api/endpoints';
import { ApiError, callDelete, callPost } from '@/api/fetcher';
import { rolePermissionsQueryKey, useRolePermissions } from '@/hooks/useRolePermissions';
import { selectIsUserOwner } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { runWithConcurrency } from '@/util/concurrency';
import {
  accessToPermissions,
  diffPermissions,
  isScopeIncomplete,
  OWNER_ONLY_PERMISSIONS,
  ownerOnlyLabelKeys,
  permissionsToAccess,
  PermissionGrant,
  presetAccess,
  RoleAccess,
  stripOwnerOnly,
  uncataloguedGrants,
} from '@/util/roleAccess';

const MAX_IN_FLIGHT = 6;

const NOTHING_PROTECTED: string[] = [];

const NOTHING_DROPPED: string[] = [];

const API = {
  addPermission: (role_id: string, { permission, resource_id }: PermissionGrant) =>
    callPost(endpoints.rolePermissionsBase(role_id), { permission, allow: true, resource_id }),
  removePermission: (role_id: string, role_permission_id: string) =>
    callDelete(endpoints.rolePermission(role_id, role_permission_id)),
};

type SaveResult = { failed: number; roleId: string };

type Options = {
  isNewRole?: boolean;
  pendingName?: string;
  pendingDescription?: string;
  sourceRoleId?: string;
  createRole?: (payload: { name: string; description: string }) => Promise<{ role_id: string }>;
  onCreated?: (roleId: string) => void;
};

export const useRoleAccess = (
  roleId?: string,
  { isNewRole, pendingName, pendingDescription, sourceRoleId, createRole, onCreated }: Options = {}
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const isUserOwner = useSelector(selectIsUserOwner);

  const isUnsaved = !!pendingName;
  const isNew = !!isNewRole || isUnsaved;
  const createdRoleId = useRef<string>(undefined);

  const { permissions, hasData, listStatus, isLoading, isTruncated } = useRolePermissions(
    isNew ? undefined : roleId
  );

  const isCopy = isUnsaved && !!sourceRoleId;
  const source = useRolePermissions(isCopy ? sourceRoleId : undefined);

  const isLoaded = isCopy ? source.hasData : isNew || hasData;

  const storedAccess = useMemo(() => permissionsToAccess(permissions), [permissions]);

  const [draft, setDraft] = useState<{ roleId?: string; access: RoleAccess } | null>(null);

  const sourceAccess = useMemo(
    () => (source.hasData ? permissionsToAccess(source.permissions) : null),
    [source.hasData, source.permissions]
  );

  const droppedOwnerOnlyLabels = useMemo(
    () => (isCopy && !isUserOwner ? ownerOnlyLabelKeys(source.permissions) : NOTHING_DROPPED),
    [isCopy, isUserOwner, source.permissions]
  );

  const suggested = useMemo(() => {
    if (!isUnsaved) {
      return null;
    }
    if (!isCopy) {
      return presetAccess();
    }
    if (!sourceAccess) {
      return null;
    }
    return isUserOwner ? sourceAccess : stripOwnerOnly(sourceAccess);
  }, [isCopy, isUnsaved, isUserOwner, sourceAccess]);
  const current =
    (draft && draft.roleId === roleId ? draft.access : null) ?? suggested ?? storedAccess;

  const edit = useCallback(
    (change: (access: RoleAccess) => RoleAccess) =>
      setDraft((prev) => ({
        roleId,
        access: change(prev && prev.roleId === roleId ? prev.access : current),
      })),
    [current, roleId]
  );

  const storedKey = useMemo(
    () => JSON.stringify(accessToPermissions(storedAccess)),
    [storedAccess]
  );

  const isDirty = useMemo(
    () => isLoaded && (isUnsaved || JSON.stringify(accessToPermissions(current)) !== storedKey),
    [current, isLoaded, isUnsaved, storedKey]
  );

  const protectedPermissions = isUserOwner ? NOTHING_PROTECTED : OWNER_ONLY_PERMISSIONS;

  const save = useMutation<SaveResult, ApiError, void>({
    mutationFn: async () => {
      if (isUnsaved && !createRole) {
        throw new Error('Cannot create a role without a createRole handler');
      }
      if (!roleId && !isUnsaved) {
        throw new Error('Cannot save permissions without a role');
      }
      if (isUnsaved && !createdRoleId.current) {
        createdRoleId.current = (
          await createRole({ name: pendingName, description: pendingDescription ?? '' })
        ).role_id;
      }
      const targetId = isUnsaved ? createdRoleId.current : roleId;

      const desired = [
        ...accessToPermissions(current),
        ...(isCopy ? uncataloguedGrants(source.permissions) : []),
      ];

      const { added, removed } = diffPermissions(permissions, desired, protectedPermissions);

      const removals = await runWithConcurrency(
        removed.map((row) => () => API.removePermission(targetId, row.role_permission_id)),
        MAX_IN_FLIGHT,
        (write) => write()
      );
      const failedRemovals = removals.filter((result) => result.status === 'rejected');

      const additions = failedRemovals.length
        ? []
        : await runWithConcurrency(
            added.map((grant) => () => API.addPermission(targetId, grant)),
            MAX_IN_FLIGHT,
            (write) => write()
          );

      const rejected = [...failedRemovals, ...additions.filter((r) => r.status === 'rejected')];
      const failed = rejected.length + (failedRemovals.length ? added.length : 0);
      const attempted = removals.length + (failedRemovals.length ? added.length : additions.length);

      if (failed > 0 && failed === attempted && !createdRoleId.current) {
        throw rejected[0].reason;
      }
      rejected.forEach((result) =>
        console.error('Failed to write a role permission', result.reason)
      );

      return { failed, roleId: targetId };
    },
    onMutate: () =>
      queryClient.cancelQueries({ queryKey: rolePermissionsQueryKey(roleId ?? '', accountId) }),
    onSuccess: async ({ failed, roleId: savedRoleId }) => {
      if (failed > 0) {
        toast.error(t('roles.permissions_partially_saved', { count: failed }));
      } else {
        toast.success(isUnsaved ? t('roles.custom_role_created') : t('roles.permissions_saved'));
      }
      if (isUnsaved) {
        onCreated?.(savedRoleId);
      }
      await queryClient.invalidateQueries({
        queryKey: rolePermissionsQueryKey(savedRoleId, accountId),
      });
      if (!isUnsaved && failed === 0) {
        setDraft(null);
      }
    },
    onError: (error) => {
      if (isUnsaved && !createdRoleId.current) {
        return;
      }
      toast.error(
        error?.statusCode === 403
          ? t('roles.permissions_forbidden')
          : t('roles.permissions_save_error')
      );
    },
  });

  const setLevel = useCallback(
    (groupKey: string, level: RoleAccess['levels'][string]) =>
      edit((base) => ({ ...base, levels: { ...base.levels, [groupKey]: level } })),
    [edit]
  );

  const setToggle = useCallback(
    (toggleKey: string, checked: boolean) =>
      edit((base) => ({ ...base, toggles: { ...base.toggles, [toggleKey]: checked } })),
    [edit]
  );

  const setConversationScope = useCallback(
    (conversationScope: RoleAccess['conversationScope']) =>
      edit((base) => ({ ...base, conversationScope })),
    [edit]
  );

  const setAiAgentConversations = (aiAgentConversations: boolean) =>
    edit((base) => ({ ...base, aiAgentConversations }));

  const setEnvironmentScope = useCallback(
    (environmentScope: RoleAccess['environmentScope']) =>
      edit((base) => ({ ...base, environmentScope })),
    [edit]
  );

  const setEnvironmentIds = useCallback(
    (environmentIds: string[]) => edit((base) => ({ ...base, environmentIds })),
    [edit]
  );

  return {
    access: current,
    isScopeIncomplete: isScopeIncomplete(current),
    isLoading: isLoading || source.isLoading,
    hasError:
      (listStatus === 'error' && !hasData) ||
      (source.listStatus === 'error' && !source.hasData) ||
      isTruncated ||
      source.isTruncated,
    droppedOwnerOnlyLabels,
    isDirty,
    setLevel,
    setToggle,
    setConversationScope,
    setAiAgentConversations,
    setEnvironmentScope,
    setEnvironmentIds,
    save: save.mutate,
    saveAsync: save.mutateAsync,
    isSaving: save.status === 'pending',
  };
};
